import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

/**
 * Redeems an invite / reset / magic link on our own domain.
 *
 * Supabase's default emails link straight to <project>.supabase.co, so a branded
 * message from send.signedoff.com.au carried a "set your password" button
 * pointing at an unrelated domain — the shape of an email that Microsoft
 * Defender quarantines. With `{{ .TokenHash }}` in the template the link stays
 * on app.signedoff.com.au and this page exchanges the token for a session.
 */
export function AuthConfirmPage() {
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const ran = useRef(false);

  useEffect(() => {
    // React runs effects twice in dev; the token is single-use, so a second
    // attempt would fail and show an error for a link that actually worked.
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const tokenHash = params.get('token_hash');
    const type = params.get('type') || 'invite';

    (async () => {
      // No token_hash means an older-style link, where supabase-js has already
      // taken the session out of the URL fragment. Nothing to redeem.
      if (!tokenHash) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) return navigate('/', { replace: true });
        return setError('This link is missing its sign-in code. Ask for a new one.');
      }

      const { error: err } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
      if (err) {
        setError(
          /expired|invalid|not found/i.test(err.message)
            ? 'This link has already been used or has expired. Ask your employer for a new one, or use "Forgot password" on the sign-in page.'
            : err.message,
        );
        return;
      }

      // replace: true keeps the one-time token out of the back button
      navigate(type === 'recovery' ? '/account' : '/', { replace: true });
    })();
  }, [navigate]);

  return (
    <div className="login-bg">
      <div className="login-card" style={{ textAlign: 'center' }}>
        <div className="login-title">Signed<em>Off</em></div>
        {error ? (
          <>
            <div className="login-sub">Link didn't work</div>
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7, margin: '18px 0 22px' }}>
              {error}
            </p>
            <button className="btn" style={{ width: '100%' }} onClick={() => navigate('/', { replace: true })}>
              Go to Sign In
            </button>
          </>
        ) : (
          <>
            <div className="login-sub">Signing you in…</div>
            <div className="spinner" style={{ padding: '28px 0' }}><div className="spin" /></div>
          </>
        )}
      </div>
    </div>
  );
}
